import React, { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { ChevronRight, Home } from 'lucide-react';
import {
    isWorkspaceDragEvent,
    parseWorkspaceDragPayload,
    WORKSPACE_HOVER_NAV_MS,
    type WorkspaceDragPayload,
} from '../utils/workspaceDrag';
import { categoryRootFolderPath } from '../utils/workspacePaths';
import type { WorkspaceFolderCrumb } from '../utils/workspaceTree';

export type DriveLocation =
    | { kind: 'root' }
    | { kind: 'category'; categoryKey: string; categoryLabel: string }
    | {
        kind: 'folder';
        categoryKey: string;
        categoryLabel: string;
        folderName: string;
        folderPath: string;
        ancestors: WorkspaceFolderCrumb[];
    };

interface DriveBreadcrumbProps {
    location: DriveLocation;
    onNavigate: (next: DriveLocation) => void;
    /** Drop files or folders onto the category segment to move into that category’s default folder (parent of subfolders). */
    onWorkspaceDrop?: (targetFolderPath: string, payload: WorkspaceDragPayload) => void;
}

function pathsEqual(a: string, b: string) {
    return a.replace(/\/+$/, '') === b.replace(/\/+$/, '');
}

export default function DriveBreadcrumb({ location, onNavigate, onWorkspaceDrop }: DriveBreadcrumbProps) {
    const [categoryDropActive, setCategoryDropActive] = useState(false);
    const [pendingNavSegment, setPendingNavSegment] = useState<string | null>(null);

    const navTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const scheduledSegmentRef = useRef<string | null>(null);

    const categoryParentPath =
        location.kind === 'category' || location.kind === 'folder'
            ? categoryRootFolderPath(location.categoryKey)
            : null;

    const clearHoverNavTimer = useCallback(() => {
        if (navTimerRef.current) {
            clearTimeout(navTimerRef.current);
            navTimerRef.current = null;
        }
        scheduledSegmentRef.current = null;
        setPendingNavSegment(null);
    }, []);

    const scheduleHoverNavigate = useCallback(
        (segmentId: string, next: DriveLocation) => {
            if (!onWorkspaceDrop) return;
            if (scheduledSegmentRef.current === segmentId && navTimerRef.current) return;

            clearHoverNavTimer();
            scheduledSegmentRef.current = segmentId;
            setPendingNavSegment(segmentId);

            navTimerRef.current = setTimeout(() => {
                onNavigate(next);
                clearHoverNavTimer();
            }, WORKSPACE_HOVER_NAV_MS);
        },
        [onWorkspaceDrop, onNavigate, clearHoverNavTimer],
    );

    useEffect(() => {
        const onDragEnd = () => clearHoverNavTimer();
        window.addEventListener('dragend', onDragEnd);
        return () => window.removeEventListener('dragend', onDragEnd);
    }, [clearHoverNavTimer]);

    const handleDocumentsDragOver = (e: DragEvent) => {
        if (!onWorkspaceDrop || !isWorkspaceDragEvent(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (location.kind === 'root') return;
        scheduleHoverNavigate('documents', { kind: 'root' });
    };

    const handleDocumentsDragLeave = (e: DragEvent) => {
        if (!onWorkspaceDrop) return;
        const next = e.relatedTarget as Node | null;
        if (next && e.currentTarget.contains(next)) return;
        clearHoverNavTimer();
    };

    const handleCategoryDragOver = (e: DragEvent) => {
        if (!onWorkspaceDrop || !categoryParentPath) return;
        if (!isWorkspaceDragEvent(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setCategoryDropActive(true);

        if (location.kind === 'folder') {
            scheduleHoverNavigate('category', {
                kind: 'category',
                categoryKey: location.categoryKey,
                categoryLabel: location.categoryLabel,
            });
        }
    };

    const handleCategoryDragLeave = (e: DragEvent) => {
        setCategoryDropActive(false);
        const next = e.relatedTarget as Node | null;
        if (next && e.currentTarget.contains(next)) return;
        clearHoverNavTimer();
    };

    const handleCategoryDrop = (e: DragEvent) => {
        clearHoverNavTimer();
        setCategoryDropActive(false);
        if (!onWorkspaceDrop || !categoryParentPath) return;
        const payload = parseWorkspaceDragPayload(e);
        if (!payload) return;
        e.preventDefault();
        if (payload.kind === 'folder' && pathsEqual(payload.sourceFolderPath, categoryParentPath)) {
            return;
        }
        onWorkspaceDrop(categoryParentPath, payload);
    };

    const documentsNavHighlight =
        pendingNavSegment === 'documents' && location.kind !== 'root'
            ? 'ring-2 ring-amber-400 bg-amber-50/90'
            : '';

    const categoryNavHighlight =
        pendingNavSegment === 'category' && location.kind === 'folder'
            ? 'ring-2 ring-amber-400 bg-amber-50/90'
            : '';
    const folderCrumbs =
        location.kind === 'folder'
            ? [...location.ancestors, { name: location.folderName, folderPath: location.folderPath }]
            : [];

    return (
        <nav
            className="flex flex-wrap items-center gap-1.5 text-sm text-gray-600 px-1"
            aria-label="Folder path"
        >
            <button
                type="button"
                onClick={() => onNavigate({ kind: 'root' })}
                onDragOver={onWorkspaceDrop ? handleDocumentsDragOver : undefined}
                onDragLeave={onWorkspaceDrop ? handleDocumentsDragLeave : undefined}
                title={
                    onWorkspaceDrop && location.kind !== 'root'
                        ? 'Hover to open Documents, then drop into any folder'
                        : undefined
                }
                className={`inline-flex items-center gap-2 rounded-md px-2 py-1 text-gray-800 font-medium transition-colors hover:bg-gray-100 focus:outline-none focus:ring-0 ${documentsNavHighlight}`}
            >
                <Home className="w-4 h-4 shrink-0" />
                Documents
            </button>
            {location.kind !== 'root' && (
                <>
                    <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                    <button
                        type="button"
                        onClick={() =>
                            onNavigate({
                                kind: 'category',
                                categoryKey: location.categoryKey,
                                categoryLabel: location.categoryLabel,
                            })
                        }
                        onDragOver={onWorkspaceDrop ? handleCategoryDragOver : undefined}
                        onDragLeave={onWorkspaceDrop ? handleCategoryDragLeave : undefined}
                        onDrop={onWorkspaceDrop ? handleCategoryDrop : undefined}
                        title={
                            onWorkspaceDrop && categoryParentPath
                                ? location.kind === 'folder'
                                    ? `Hover to open this category, or drop to move into: ${categoryParentPath}`
                                    : `Drop here to move into category folder: ${categoryParentPath}`
                                : undefined
                        }
                        className={`max-w-[min(100%,14rem)] truncate rounded-md px-2 py-1 text-left transition-colors focus:outline-none focus:ring-0 ${(categoryDropActive && onWorkspaceDrop)
                                ? 'bg-indigo-100 ring-2 ring-indigo-400 text-indigo-950'
                                : categoryNavHighlight
                                    ? categoryNavHighlight
                                : onWorkspaceDrop
                                    ? 'hover:bg-gray-100 cursor-default'
                                    : 'hover:bg-gray-100'
                            }`}
                    >
                        {location.categoryLabel}
                    </button>
                </>
            )}
            {location.kind === 'folder' &&
                folderCrumbs.map((crumb, index) => {
                    const isCurrent = index === folderCrumbs.length - 1;
                    const nextLocation: DriveLocation = {
                        kind: 'folder',
                        categoryKey: location.categoryKey,
                        categoryLabel: location.categoryLabel,
                        folderName: crumb.name,
                        folderPath: crumb.folderPath,
                        ancestors: folderCrumbs.slice(0, index),
                    };
                    return (
                        <React.Fragment key={crumb.folderPath}>
                            <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                            {isCurrent ? (
                                <span className="text-gray-900 font-medium truncate max-w-[min(100%,14rem)] px-2 py-1">
                                    {crumb.name}
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => onNavigate(nextLocation)}
                                    className="max-w-[min(100%,14rem)] truncate rounded-md px-2 py-1 text-left transition-colors hover:bg-gray-100 focus:outline-none focus:ring-0"
                                >
                                    {crumb.name}
                                </button>
                            )}
                        </React.Fragment>
                    );
                })}
        </nav>
    );
}
